'use client';

// EIP-712 order signing for Polymarket CTF Exchange.
//
// The signed payload follows the canonical CLOB v2 Order struct.
// `maker` and `signer` are both the EOA — Polymarket also supports
// proxy/safe signing modes but our paste-the-EOA flow is signatureType=0.
//
// Source for the struct + domain: Polymarket clob-client/src/signing.

import type { Account, Hex } from 'viem';
import {
  CTF_EXCHANGE_ADDRESS,
  POLYGON_CHAIN_ID,
} from '../polymarket/contracts.js';

const ORDER_DOMAIN = {
  name: 'Polymarket CTF Exchange',
  version: '1',
  chainId: POLYGON_CHAIN_ID,
  verifyingContract: CTF_EXCHANGE_ADDRESS,
} as const;

const ORDER_TYPES = {
  Order: [
    { name: 'salt', type: 'uint256' },
    { name: 'maker', type: 'address' },
    { name: 'signer', type: 'address' },
    { name: 'taker', type: 'address' },
    { name: 'tokenId', type: 'uint256' },
    { name: 'makerAmount', type: 'uint256' },
    { name: 'takerAmount', type: 'uint256' },
    { name: 'expiration', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'feeRateBps', type: 'uint256' },
    { name: 'side', type: 'uint8' },
    { name: 'signatureType', type: 'uint8' },
  ],
} as const;

const ZERO_ADDR = '0x0000000000000000000000000000000000000000' as const;
const TOKEN_DECIMALS = 6;
const USDC_DECIMALS = 6;

export interface PolymarketSignedOrder {
  salt: string;
  maker: `0x${string}`;
  signer: `0x${string}`;
  taker: `0x${string}`;
  tokenId: string;
  makerAmount: string;
  takerAmount: string;
  expiration: string;
  nonce: string;
  feeRateBps: string;
  side: number;
  signatureType: number;
  signature: Hex;
}

export interface BuildOrderInput {
  // Outcome token id — comes through as `outcomeId` in the normalized
  // order. Must be the on-chain ERC-1155 token id (a uint256 string).
  tokenId: string;
  // Price in [0, 1] (e.g. 0.62 = 62%).
  price: number;
  // Number of outcome tokens (the size we ship to the venue).
  size: number;
  // 'buy' (BUY = 0) or 'sell' (SELL = 1).
  side: 'buy' | 'sell';
  // GTC = 0 (rest indefinitely), or a unix-seconds expiration for IOC/FOK.
  expiration?: number;
  // Per-order fee rate in bps. PM uses 0 today.
  feeRateBps?: number;
}

function randomSalt(): bigint {
  // 32 random bytes → uint256
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let v = 0n;
  for (let i = 0; i < bytes.length; i++) {
    v = (v << 8n) | BigInt(bytes[i]!);
  }
  return v;
}

// Convert a fractional value to 6-decimal fixed-point bigint, rounding.
// We snap to integer counts of tokens and to the nearest cent on price
// because that's what Polymarket's order matcher expects.
function toUnits(value: number, decimals: number): bigint {
  const scaled = Math.round(value * 10 ** decimals);
  if (!Number.isFinite(scaled) || scaled < 0) throw new Error(`invalid amount ${value}`);
  return BigInt(scaled);
}

export async function signPolymarketOrder(
  account: Account,
  input: BuildOrderInput,
): Promise<PolymarketSignedOrder> {
  if (input.price <= 0 || input.price >= 1) throw new Error('price must be in (0,1)');
  if (input.size <= 0) throw new Error('size must be > 0');

  const tokenAmount = toUnits(input.size, TOKEN_DECIMALS);
  const usdcAmount = toUnits(input.size * input.price, USDC_DECIMALS);
  const isBuy = input.side === 'buy';

  // For BUY: maker pays USDC, taker pays outcome tokens. So
  //   makerAmount = USDC amount, takerAmount = outcome token count.
  // For SELL: reverse.
  const makerAmount = isBuy ? usdcAmount : tokenAmount;
  const takerAmount = isBuy ? tokenAmount : usdcAmount;

  const salt = randomSalt();
  const expiration = BigInt(input.expiration ?? 0);
  const feeRateBps = BigInt(input.feeRateBps ?? 0);
  const nonce = 0n;

  const message = {
    salt,
    maker: account.address,
    signer: account.address,
    taker: ZERO_ADDR as `0x${string}`,
    tokenId: BigInt(input.tokenId),
    makerAmount,
    takerAmount,
    expiration,
    nonce,
    feeRateBps,
    side: isBuy ? 0 : 1,
    signatureType: 0,
  };

  if (!account.signTypedData) {
    throw new Error('viem account is missing signTypedData');
  }
  const signature = await account.signTypedData({
    domain: ORDER_DOMAIN,
    types: ORDER_TYPES,
    primaryType: 'Order',
    message,
  });

  return {
    salt: salt.toString(),
    maker: account.address,
    signer: account.address,
    taker: ZERO_ADDR,
    tokenId: input.tokenId,
    makerAmount: makerAmount.toString(),
    takerAmount: takerAmount.toString(),
    expiration: expiration.toString(),
    nonce: nonce.toString(),
    feeRateBps: feeRateBps.toString(),
    side: message.side,
    signatureType: 0,
    signature,
  };
}

export type PolymarketOrderType = 'GTC' | 'IOC' | 'FOK';

export interface PolymarketSubmitPayload {
  order: PolymarketSignedOrder;
  owner: string; // L2 api key
  orderType: PolymarketOrderType;
}
